import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { F } from "../fonts";

const C = {
  vanilla:  "#F8F4EE",
  softDune: "#E4DDCC",
  taupe:    "#C5B49D",
  umber:    "#443A35",
  black:    "#252525",
};

const fields = [
  { name: "name",    label: "Name",         type: "text",     multiline: false },
  { name: "email",   label: "Mail ID",      type: "email",    multiline: false },
  { name: "role",    label: "What you do", type: "text",     multiline: false },
  { name: "message", label: "Message",      type: "text",     multiline: true  },
];

const ContactPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", role: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (name) => (e) => {
    setForm((prev) => ({ ...prev, [name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": process.env.REACT_APP_PORTFOLIO_API_KEY,
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          subject: form.role,
          message: form.message,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Something went wrong. Please try again.");
      }

      setStatus("success");
      setForm({ name: "", email: "", role: "", message: "" });
    } catch (err) {
      setStatus("error");
      setErrorMessage(err.message);
    }
  };

  return (
    <Box sx={{ backgroundColor: C.vanilla, minHeight: "100vh" }}>
      {/* ── Top bar ── */}
      <Box sx={{
        px: { xs: "20px", md: "30px" }, py: "22px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: `1px solid ${C.taupe}`,
        position: "sticky", top: 0, zIndex: 100,
        backgroundColor: C.vanilla,
      }}>
        <Box
          onClick={() => navigate("/")}
          sx={{
            display: "flex", alignItems: "center", gap: 1.5,
            cursor: "pointer", color: C.umber,
            transition: "color 0.2s ease",
            "&:hover": { color: C.black },
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/>
            <polyline points="12 19 5 12 12 5"/>
          </svg>
          <Typography sx={{ fontSize: "12px", letterSpacing: "2.5px", fontFamily: F.heading }}>BACK</Typography>
        </Box>
        <Typography sx={{ fontSize: "12px", letterSpacing: "3px", fontFamily: F.heading, color: C.taupe }}>
          HARIDHARSHINI JAYARAJ
        </Typography>
      </Box>

      {/* ── Page title ── */}
      <Box sx={{ px: { xs: "20px", md: "30px" }, pt: "50px", pb: "20px" }}>
        <Typography sx={{ fontSize: { xs: "36px", md: "52px" }, fontWeight: 400, letterSpacing: "2px", color: C.black }}>
          /contact
        </Typography>
        <Typography sx={{ mt: 1.5, fontSize: "15px", fontFamily: F.body, color: C.umber, maxWidth: "560px" }}>
          Drop a message to collaborate — I'll get back to you soon.
        </Typography>
      </Box>

      {/* ── Form ── */}
      <Box sx={{ px: { xs: "20px", md: "30px" }, pb: "100px" }}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            maxWidth: "560px",
            display: "flex", flexDirection: "column", gap: 3,
            mt: 3,
          }}
        >
          {fields.map(({ name, label, type, multiline }) => (
            <Box key={name} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography sx={{ fontSize: "13px", letterSpacing: "1.5px", fontFamily: F.heading, color: C.black }}>
                {label.toUpperCase()}
              </Typography>
              <Box
                component={multiline ? "textarea" : "input"}
                type={multiline ? undefined : type}
                required
                rows={multiline ? 5 : undefined}
                value={form[name]}
                onChange={handleChange(name)}
                sx={{
                  fontFamily: F.body,
                  fontSize: "15px",
                  color: C.black,
                  backgroundColor: "transparent",
                  border: "none",
                  borderBottom: `1px solid ${C.taupe}`,
                  outline: "none",
                  py: 1,
                  resize: multiline ? "vertical" : "none",
                  transition: "border-color 0.2s ease",
                  "&:focus": { borderColor: C.umber },
                }}
              />
            </Box>
          ))}

          <Box
            component="button"
            type="submit"
            disabled={status === "submitting"}
            sx={{
              alignSelf: "flex-start",
              mt: 2,
              px: 4, py: 1.3,
              border: `1px solid ${C.black}`,
              borderRadius: "30px",
              backgroundColor: "transparent",
              cursor: status === "submitting" ? "default" : "pointer",
              opacity: status === "submitting" ? 0.6 : 1,
              fontSize: "13px", letterSpacing: "2px", fontFamily: F.heading,
              color: C.black,
              transition: "background-color 0.2s ease, color 0.2s ease",
              "&:hover": status === "submitting" ? {} : { backgroundColor: C.black, color: C.vanilla },
            }}
          >
            {status === "submitting" ? "SENDING…" : "SEND MESSAGE"}
          </Box>

          {status === "success" && (
            <Typography sx={{ fontSize: "13px", fontFamily: F.body, color: C.umber }}>
              Thanks — your message has been sent. I'll get back to you soon.
            </Typography>
          )}
          {status === "error" && (
            <Typography sx={{ fontSize: "13px", fontFamily: F.body, color: "#b3261e" }}>
              {errorMessage}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ContactPage;
